import React from 'react';
import PropTypes from 'prop-types';
import { FaBook, FaCalendarAlt, FaGlobeEurope, FaMusic } from 'react-icons/fa';
import { localeFor, useTranslation } from '../../utils/translations';
import Avatar from '../Common/Avatar';
import TeacherContacts from './TeacherContacts';
import { dayKey, peopleContacts } from './familyCompat';
import './ActivityGroupedView.css';

const iconForActivity = (id) => {
  switch (id) {
    case 'sundaySchool':
      return <FaBook aria-hidden="true" />;
    case 'russianSchool':
      return <FaGlobeEurope aria-hidden="true" />;
    case 'childrensChoir':
      return <FaMusic aria-hidden="true" />;
    default:
      return null;
  }
};

// Format birthdate for display, in the app language's locale
const formatBirthdate = (birthdate, language) => {
  if (!birthdate) return '';
  try {
    const [year, month, day] = birthdate.split('-').map(Number);
    const date = new Date(year, month - 1, day); // local date, not UTC
    return date.toLocaleDateString(localeFor(language), {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch (error) {
    return birthdate; // Return original if parsing fails
  }
};

// Whole years since the birthdate, or null when it cannot be parsed
const ageOf = (birthdate) => {
  try {
    const [y, m, d] = birthdate.split('-').map(Number);
    const now = new Date();
    let age = now.getFullYear() - y;
    if (now.getMonth() + 1 < m || (now.getMonth() + 1 === m && now.getDate() < d)) age -= 1;
    return Number.isFinite(age) ? age : null;
  } catch (error) {
    return null;
  }
};

const ActivityGroupedView = ({ activities, students }) => {
  const { t, tn, language } = useTranslation();
  
  // index activities by id for fast access
  const activityMap = activities.reduce((acc, a) => {
    acc[a.id] = a;
    return acc;
  }, {});

  // Helper to get a student's activity details by id
  const getStudentActivity = (student, activityId) =>
    student.activities.find((sa) => sa.id === activityId);

  return (
    <div className="activity-grouped-layout" role="main" aria-label={t('details.activitiesGroupedView')}>
      {['sundaySchool', 'russianSchool', 'childrensChoir'].map((aid) => {
        const activity = activityMap[aid];
        const studentsWithActivity = students.filter(student => {
          const activity = getStudentActivity(student, aid);
          // Check if activity exists and has meaningful details
          return activity?.details && 
                 typeof activity.details === 'object' && 
                 Object.keys(activity.details).length > 0;
        });

        // Don't render activity section if no students are enrolled
        if (studentsWithActivity.length === 0) {
          return null;
        }

        return (
          <div key={aid} className="activity-container" data-activity={aid}>
            <div className="activity-container-header">
              <span className="activity-icon">{iconForActivity(aid)}</span>
              <h3 className="activity-title">{t(`activities.${aid}`) || activity?.name || aid}</h3>
            </div>
            <div className="students-in-activity">
              {studentsWithActivity.map((student) => {
                const sa = getStudentActivity(student, aid);
                const d = sa?.details || {};
                const room = d.classroom || d.choirRoom;
                const main = d.class || d.choirNumber;
                // Names plus phone numbers where the payload carries them
                const people = peopleContacts(d);
                const labels = aid === 'childrensChoir'
                  ? { main: t('details.choirNumber'), people: t('details.choirLeaders') }
                  : { main: t('details.class'), people: t('details.teachers') };

                return (
                  <div key={student.id} className="student-activity-card">
                    <div className="student-info">
                      <Avatar name={student.name} gender={student.gender} />
                      <h4 className="student-name">{student.name}</h4>
                      {d.day && (
                        <span className="day-badge"><FaCalendarAlt aria-hidden="true" /> {dayKey(d.day) ? t(dayKey(d.day)) : d.day}</span>
                      )}
                    </div>
                    <div className="activity-details">
                      <div className="detail-row">
                        <span className="detail-label">{labels.main}:</span>
                        <span className="detail-value">{main}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">{t('details.room')}:</span>
                        <span className="detail-value">{room}</span>
                      </div>
                      {people.length > 0 && (
                        <div className="detail-row">
                          <span className="detail-label">{labels.people}:</span>
                          <span className="detail-value">
                            <TeacherContacts people={people} />
                          </span>
                        </div>
                      )}
                      {student.birthdate && (
                        <div className="detail-row">
                          <span className="detail-label">{t('details.birthday')}:</span>
                          <span className="detail-value">
                            {formatBirthdate(student.birthdate, language)}
                            {ageOf(student.birthdate) !== null && ` · ${tn('details.age', ageOf(student.birthdate))}`}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

ActivityGroupedView.propTypes = {
  students: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      activities: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          status: PropTypes.string,
          details: PropTypes.object,
        })
      ).isRequired,
    })
  ).isRequired,
  activities: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      day: PropTypes.string,
    })
  ).isRequired,
};

export default ActivityGroupedView;
